/**
 * Template Editor Container
 *
 * Integrates TemplateEditor with Firestore via useTemplatesManager
 * Handles loading/saving automatically
 */

import { Alert, LoadingOverlay, Stack } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useMemo } from "react";
import { TemplateEditor } from "./TemplateEditor";
import { useTemplatesManager } from "../../hooks/useTemplatesManager";

/** Save state exposed to parent */
export interface SaveState {
  isSaving: boolean;
  isValid: boolean;
  save: () => void;
}

export interface TemplateEditorContainerProperties {
  /** Template ID to load/edit */
  templateId: string;
  /** Callback when save succeeds */
  onSaveSuccess?: () => void;
  /** Callback when save fails */
  onSaveError?: (error: Error) => void;
  /** Show variable helper panel (default: true) */
  showVariableHelper?: boolean;
  /** Questionário ID for loading real grupos in preview */
  questionarioId?: string;
  /** Hide internal save button (useful when rendering save button externally) */
  hideSaveButton?: boolean;
  /** Callback to expose save state to parent */
  onSaveStateChange?: (state: SaveState) => void;
}

/**
 * TemplateEditorContainer - Integrates editor with Firestore
 * @param root0
 * @param root0.templateId
 * @param root0.onSaveSuccess
 * @param root0.onSaveError
 * @param root0.showVariableHelper
 * @param root0.questionarioId
 * @param root0.hideSaveButton
 * @param root0.onSaveStateChange
 */
export function TemplateEditorContainer({
  templateId,
  onSaveSuccess,
  onSaveError,
  showVariableHelper = true,
  questionarioId,
  hideSaveButton = false,
  onSaveStateChange
}: TemplateEditorContainerProperties) {
  const { templates, isLoading, updateTemplate } = useTemplatesManager();

  // Find template using useMemo (avoids re-render loop)
  const template = useMemo(() => {
    if (isLoading) return null;
    return templates.find((t) => t.id === templateId) || null;
  }, [templates, templateId, isLoading]);

  // Check for load error
  const loadError = useMemo(() => {
    if (isLoading) return null;
    if (!template) return `Template com ID "${templateId}" não encontrado`;
    return null;
  }, [template, templateId, isLoading]);

  // Handle save (manual save with notification)
  const handleSave = async (htmlTemplate: string) => {
    if (!template) return;

    try {
      await updateTemplate({
        templateId,
        data: {
          nome: template.nome,
          descricao: template.descricao,
          componentPath: template.componentPath as any,
          ativo: template.ativo,
          configuracao: {
            incluirGraficos: true,
            incluirTabelas: true,
            incluirAnaliseDetalhada: true
          },
          htmlTemplate
        },
        silent: false // Show notification on manual save
      });

      onSaveSuccess?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error : new Error("Erro desconhecido ao salvar");
      onSaveError?.(errorMessage);
      throw errorMessage;
    }
  };

  // Loading state
  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  // Error state
  if (loadError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Erro ao carregar template" color="red">
        {loadError}
      </Alert>
    );
  }

  // Not found
  if (!template) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Template não encontrado" color="yellow">
        O template solicitado não foi encontrado.
      </Alert>
    );
  }

  // Render editor
  return (
    <Stack>
      <TemplateEditor
        initialHtml={template.htmlTemplate || ""}
        onSave={handleSave}
        autoSave={false}
        placeholder={`Digite o HTML do template "${template.nome}"...`}
        showVariableHelper={showVariableHelper}
        questionarioId={questionarioId}
        templateId={templateId}
        templateImages={template.imagens}
        hideSaveButton={hideSaveButton}
        onSaveStateChange={onSaveStateChange}
      />
    </Stack>
  );
}
